// @flow
import * as React from 'react';
import { FormGroup, FormControl, ControlLabel } from 'react-bootstrap';
import Toggle from 'react-toggle';
import Autocomplete from './Autocomplete';
import { uniqueId } from '../helpers';
import InfoIcon from './InfoIcon';

const sortByName = (a: Object, b: Object) => {
  const nameA = a.name.toUpperCase();
  const nameB = b.name.toUpperCase();
  if (nameA < nameB) {
    return -1;
  } else if (nameA > nameB) {
    return 1;
  }
  return 0;
};

type Props = {
  feedbackIcon: React.Component<*>,
  input: any,
  label: any,
  type: any,
  options: any,
  meta: Object,
  prompt: any,
  promptName: any,
  reverse: any,
  infoIcon: any,
  optionsKeys: string[],
  error: any,
  warning: any,
  touched: any,
  sort: ?boolean,
  placeholder: ?string,
  className: ?string,
  disabled?: boolean,
  file: ?string
};

class Input extends React.Component<Props> {
  render() {
    const {
      feedbackIcon,
      input,
      label,
      type,
      options,
      meta: { error, warning, touched },
      prompt,
      promptName,
      reverse,
      infoIcon,
      optionsKeys = ['id', 'name'],
      sort,
      ...props
    } = this.props;

    const id = uniqueId();
    let message;
    let validationState = null;
    if (touched && error) {
      validationState = 'error';
    }
    if (warning) {
      validationState = 'warning';
    }

    if (touched && (error || warning)) {
      message = <span className="help-block">{error || warning}</span>;
    }

    switch (type) {
      case 'autocomplete':
        return (
          <FormGroup validationState={validationState}>
            <ControlLabel>{label}</ControlLabel>
            {infoIcon && <InfoIcon>{infoIcon}</InfoIcon>}
            <Autocomplete
              value={input.value}
              onChange={input.onChange}
              placeholder={props.placeholder}
              disabled={props.disabled}
            />
            {message}
          </FormGroup>
        );

      case 'checkbox':
        return (
          <div className="checkbox-wrapper">
            <input type={type} {...input} id={id} className={props.className} />
            <label htmlFor={id}>{label}</label>
            {infoIcon && <InfoIcon>{infoIcon}</InfoIcon>}
          </div>
        );
      case 'select':
        return (
          <FormGroup validationState={validationState}>
            <ControlLabel>{label}</ControlLabel>
            {infoIcon && <InfoIcon>{infoIcon}</InfoIcon>}
            <FormControl {...input} type={type} {...props}>
              {prompt && (
                <option value="">{promptName || 'Please Select'}</option>
              )}
              {options &&
                sort &&
                options.sort(sortByName).map(option => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              {options &&
                !sort &&
                options.map(option => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
            </FormControl>
            {feedbackIcon ? (
              <FormControl.Feedback>{feedbackIcon}</FormControl.Feedback>
            ) : null}
            {message}
          </FormGroup>
        );

      case 'radioGroup':
        return (
          <FormGroup validationState={validationState}>
            {label && <ControlLabel>{label}</ControlLabel>}
            {infoIcon && <InfoIcon>{infoIcon}</InfoIcon>}
            {options &&
              options.map(option => (
                <ControlLabel
                  key={option[optionsKeys[0]]}
                  className="radio indent"
                >
                  <FormControl
                    {...input}
                    type="radio"
                    value={option.id}
                    checked={option.id === input.value}
                    {...props}
                  />
                  {feedbackIcon ? (
                    <FormControl.Feedback>{feedbackIcon}</FormControl.Feedback>
                  ) : null}
                  <span>{option[optionsKeys[1]]}</span>
                </ControlLabel>
              ))}
            {message}
          </FormGroup>
        );

      case 'radioSingle':
        return (
          <ControlLabel key={options.id} className="radio indent radiosingle">
            <FormControl
              {...input}
              type="radio"
              value={options.id}
              checked={options.id === input.value}
              {...props}
            />
            {feedbackIcon ? (
              <FormControl.Feedback>{feedbackIcon}</FormControl.Feedback>
            ) : null}
            <span>{options.name}</span>
          </ControlLabel>
        );

      case 'toggle':
        const inputProps = { ...input, value: undefined };
        return (
          <FormGroup validationState={validationState}>
            <label className="label-vmiddle" htmlFor={id}>
              <Toggle
                {...inputProps}
                checked={reverse ? !input.value : input.value}
                className="primary-color"
                id={id}
                icons={false}
                onChange={e =>
                  input.onChange(reverse ? !e.target.checked : e.target.checked)
                }
                disabled={props.disabled}
              />
              <span>{label}</span>
            </label>
            {feedbackIcon ? (
              <FormControl.Feedback>{feedbackIcon}</FormControl.Feedback>
            ) : null}
            {message}
            {infoIcon && <InfoIcon>{infoIcon}</InfoIcon>}
          </FormGroup>
        );

      case 'termsAndConditions':
        return (
          <FormGroup validationState={validationState}>
            <div className="checkbox-wrapper">
              <input
                type="checkbox"
                {...input}
                id={id}
                className={props.className}
              />
              <label htmlFor={id}>
                I accept the{' '}
                <a href={props.file} target="_blank">
                  Terms and Conditions
                </a>
              </label>
              {infoIcon && <InfoIcon>{infoIcon}</InfoIcon>}
            </div>
            {message}
          </FormGroup>
        );

      case 'phone':
        return (
          <FormGroup validationState={validationState}>
            <ControlLabel>{label}</ControlLabel>
            {infoIcon && <InfoIcon>{infoIcon}</InfoIcon>}
            <FormControl {...input} type={type} {...props} />
            {feedbackIcon ? (
              <FormControl.Feedback>{feedbackIcon}</FormControl.Feedback>
            ) : null}
            {message}
          </FormGroup>
        );

      case 'textarea':
        return (
          <FormGroup validationState={validationState}>
            <ControlLabel>{label}</ControlLabel>
            {infoIcon && <InfoIcon>{infoIcon}</InfoIcon>}
            <FormControl componentClass={type} {...input} {...props} />
            {feedbackIcon ? (
              <FormControl.Feedback>{feedbackIcon}</FormControl.Feedback>
            ) : null}
            {message}
          </FormGroup>
        );

      case 'hidden':
        return <FormControl {...input} type={type} {...props} />;

      default:
        return (
          <FormGroup validationState={validationState}>
            <ControlLabel>{label}</ControlLabel>
            {infoIcon && <InfoIcon>{infoIcon}</InfoIcon>}
            <FormControl {...input} type={type} {...props} />
            {feedbackIcon ? (
              <FormControl.Feedback>{feedbackIcon}</FormControl.Feedback>
            ) : null}
            {message}
          </FormGroup>
        );
    }
  }
}

export default Input;



// WEBPACK FOOTER //
// ./src/components/Input.js